import React, { useCallback, useEffect, useState } from 'react';
import { regelnApi, workflowsApi, ereignisseApi, smartDashboardApi } from '../api';

const PRIORITY_COLOR = { critical: '#ef4444', high: '#f97316', medium: '#eab308', low: '#22c55e' };
const TRIGGER_LABEL = { daily_check: 'Täglich', price_change: 'Preisänderung', event: 'Ereignis', manual: 'Manuell', immediate: 'Sofort' };
const STATUS_COLOR = { running: '#22c55e', completed: '#3b82f6', failed: '#ef4444', cancelled: '#94a3b8', paused: '#f59e0b', ready: '#8b5cf6', partially_completed: '#f97316' };

const TABS = [
  { id: 'status', label: 'Status' },
  { id: 'regeln', label: 'Automatisierungsregeln' },
  { id: 'workflows', label: 'Workflow-Runs' },
  { id: 'ereignisse', label: 'Ereignisse' },
];

export default function AutomatisierungenPage() {
  const [activeTab, setActiveTab] = useState('status');
  const [statusData, setStatusData] = useState(null);
  const [regeln, setRegeln] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [ereignisse, setEreignisse] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await smartDashboardApi.automatisierung();
      setStatusData(r.data);
    } catch { setError('Statusdaten konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  const loadRegeln = useCallback(async () => {
    setLoading(true);
    try {
      const r = await regelnApi.list();
      setRegeln(Array.isArray(r.data) ? r.data : []);
    } catch { setError('Regeln konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  const loadWorkflows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await workflowsApi.runs({ limit: 50 });
      setWorkflowRuns(Array.isArray(r.data) ? r.data : []);
    } catch { setError('Workflow-Runs konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  const loadEreignisse = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ereignisseApi.list({ limit: 50 });
      setEreignisse(Array.isArray(r.data) ? r.data : []);
    } catch { setError('Ereignisse konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (activeTab === 'status') loadStatus();
    else if (activeTab === 'regeln') loadRegeln();
    else if (activeTab === 'workflows') loadWorkflows();
    else if (activeTab === 'ereignisse') loadEreignisse();
  }, [activeTab, loadStatus, loadRegeln, loadWorkflows, loadEreignisse]);

  const handleRegel = async (id, action) => {
    try {
      if (action === 'aktivieren') await regelnApi.aktivieren(id);
      else if (action === 'deaktivieren') await regelnApi.deaktivieren(id);
      loadRegeln();
    } catch (e) { setError(e?.response?.data?.error || 'Aktion fehlgeschlagen'); }
  };

  const handleWorkflow = async (id, action) => {
    try {
      if (action === 'abbrechen') await workflowsApi.abbrechen(id, 'Manuell abgebrochen');
      else if (action === 'pausieren') await workflowsApi.pausieren(id);
      else if (action === 'fortsetzen') await workflowsApi.fortsetzen(id);
      loadWorkflows();
    } catch (e) { setError(e?.response?.data?.error || 'Aktion fehlgeschlagen'); }
  };

  const handleEreignis = async (id, action) => {
    try {
      if (action === 'wiederholen') await ereignisseApi.wiederholen(id);
      else if (action === 'ignorieren') await ereignisseApi.ignorieren(id);
      loadEreignisse();
    } catch (e) { setError(e?.response?.data?.error || 'Aktion fehlgeschlagen'); }
  };

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 24 }}>Automatisierungen</h1>

      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid #334155' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{ padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: activeTab === t.id ? 700 : 400, borderBottom: activeTab === t.id ? '2px solid #818cf8' : '2px solid transparent', color: activeTab === t.id ? '#818cf8' : '#64748b' }}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div style={{ background: '#431407', color: '#fca5a5', border: '1px solid #ef4444', padding: 12, borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {loading && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Lädt…</div>}

      {/* STATUS TAB */}
      {!loading && activeTab === 'status' && statusData && (
        <div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatusCard label="Aktive Regeln" value={statusData.regeln?.aktiv ?? 0} sub={`von ${statusData.regeln?.gesamt ?? 0}`} color="#22c55e" />
            <StatusCard label="Laufende Workflows" value={statusData.workflows?.laufend ?? 0} color="#8b5cf6" />
            <StatusCard label="Scheduler-Jobs" value={statusData.scheduler?.count ?? 0} color="#3b82f6" />
            <StatusCard label="Letzte Ereignisse" value={statusData.ereignisse?.length ?? 0} color="#f97316" />
          </div>

          <h3 style={{ fontWeight: 700, marginBottom: 10 }}>Scheduler-Jobs</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
            {(statusData.scheduler?.jobs || []).map(j => (
              <span key={j} style={{ padding: '4px 12px', background: '#1e1b4b', color: '#818cf8', borderRadius: 99, fontSize: 12 }}>{j}</span>
            ))}
          </div>
        </div>
      )}

      {/* REGELN TAB */}
      {!loading && activeTab === 'regeln' && (
        <div>
          {regeln.length === 0
            ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Keine Regeln gefunden.</div>
            : regeln.map(r => (
              <div key={r.id} style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', padding: '14px 18px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{r.name}</span>
                    {r.system_rule === 1 && <span style={{ fontSize: 10, padding: '2px 8px', background: '#334155', borderRadius: 99, color: '#94a3b8' }}>System</span>}
                    <span style={{ fontSize: 11, padding: '2px 8px', background: r.active ? '#052e16' : '#1e293b', color: r.active ? '#34d399' : '#64748b', borderRadius: 99 }}>{r.active ? 'Aktiv' : 'Inaktiv'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    {TRIGGER_LABEL[r.trigger_type] || r.trigger_type} · Priorität {r.priority}
                    {r.cooldown_minutes ? ` · Cooldown ${r.cooldown_minutes / 60}h` : ''}
                  </div>
                </div>
                {r.system_rule !== 1 && (
                  <button onClick={() => handleRegel(r.id, r.active ? 'deaktivieren' : 'aktivieren')}
                    style={{ fontSize: 12, padding: '5px 12px', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer', background: '#334155', color: '#94a3b8' }}>
                    {r.active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* WORKFLOWS TAB */}
      {!loading && activeTab === 'workflows' && (
        <div>
          {workflowRuns.length === 0
            ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Keine Workflow-Runs gefunden.</div>
            : workflowRuns.map(r => (
              <div key={r.id} style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', padding: '14px 18px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{r.definition_name || r.definition_key}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: (STATUS_COLOR[r.status] || '#e2e8f0') + '22', color: STATUS_COLOR[r.status] || '#64748b' }}>{r.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    Schritt {r.current_step || 0} · Gestartet: {new Date(r.created_at).toLocaleString('de-DE')}
                  </div>
                </div>
                {r.status === 'running' && (
                  <>
                    <button onClick={() => handleWorkflow(r.id, 'pausieren')} style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer', background: '#334155', color: '#94a3b8' }}>Pause</button>
                    <button onClick={() => handleWorkflow(r.id, 'abbrechen')} style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #ef4444', borderRadius: 6, cursor: 'pointer', background: '#431407', color: '#f87171' }}>Abbrechen</button>
                  </>
                )}
                {r.status === 'paused' && (
                  <button onClick={() => handleWorkflow(r.id, 'fortsetzen')} style={{ fontSize: 12, padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', background: '#22c55e', color: '#fff' }}>Fortsetzen</button>
                )}
              </div>
            ))
          }
        </div>
      )}

      {/* EREIGNISSE TAB */}
      {!loading && activeTab === 'ereignisse' && (
        <div>
          {ereignisse.length === 0
            ? <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Keine Ereignisse gefunden.</div>
            : ereignisse.map(e => (
              <div key={e.id} style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', padding: '14px 18px', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{e.title || e.event_type}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 99, background: '#334155', color: '#94a3b8' }}>{e.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                    {e.event_type} · {new Date(e.created_at).toLocaleString('de-DE')}
                    {e.attempts > 0 ? ` · ${e.attempts}/${e.max_attempts} Versuche` : ''}
                  </div>
                  {e.last_error && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>{e.last_error}</div>}
                </div>
                {e.status === 'failed' && (
                  <>
                    <button onClick={() => handleEreignis(e.id, 'wiederholen')} style={{ fontSize: 12, padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', background: '#3b82f6', color: '#fff' }}>Wiederholen</button>
                    <button onClick={() => handleEreignis(e.id, 'ignorieren')} style={{ fontSize: 12, padding: '5px 10px', border: '1px solid #334155', borderRadius: 6, cursor: 'pointer', background: '#334155', color: '#94a3b8' }}>Ignorieren</button>
                  </>
                )}
              </div>
            ))
          }
        </div>
      )}
    </div>
  );
}

function StatusCard({ label, value, sub, color }) {
  return (
    <div style={{ background: '#1e293b', borderRadius: 10, border: '1px solid #334155', padding: '16px 24px', minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <span style={{ fontSize: 28, fontWeight: 800, color: color || '#f1f5f9' }}>{value}</span>
      {sub && <span style={{ fontSize: 12, color: '#94a3b8' }}>{sub}</span>}
      <span style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>{label}</span>
    </div>
  );
}
